import { useState } from "react";
import { AppShell } from "@/components/Nav";
import { PlusGate } from "@/components/PlusGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Home, Sparkles, TrendingUp, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function MortgageSim() {
  const { user } = useAuth();
  const isPlus = user?.plan === "plus";

  const [form, setForm] = useState({
    current_score: "680",
    target_home_price: "400000",
    down_payment_pct: "20",
    monthly_debts: "400",
    monthly_income: "6500",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const payload = {
        current_score: parseInt(form.current_score, 10),
        target_home_price: parseFloat(form.target_home_price),
        down_payment_pct: parseFloat(form.down_payment_pct),
        monthly_debts: parseFloat(form.monthly_debts),
        monthly_income: parseFloat(form.monthly_income),
      };
      const { data } = await api.post("/plus/mortgage-sim", payload);
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell>
      <div className="mb-10">
        <div className="overline mb-3 text-[#D5FF3F]">Plus tool</div>
        <h1 className="font-display text-5xl md:text-6xl leading-none">Mortgage-readiness simulator.</h1>
        <p className="text-[#A8ADA5] mt-3 max-w-2xl">
          Model FHA vs conventional loans against today's US rates using your credit, down payment, and monthly obligations.
        </p>
      </div>

      {!isPlus ? (
        <PlusGate
          toolName="The mortgage simulator"
          tagline="See exactly what score + savings you need to qualify — and how many points a clean credit report is worth in monthly payments. Plus subscribers get unlimited runs."
        />
      ) : (
        <>
          <form onSubmit={run} className="card-surface p-8 grid md:grid-cols-5 gap-4 mb-6" data-testid="mortgage-form">
            {[
              { key: "current_score", label: "Current FICO", ph: "680", type: "number" },
              { key: "target_home_price", label: "Home price ($)", ph: "400000", type: "number" },
              { key: "down_payment_pct", label: "Down payment %", ph: "20", type: "number" },
              { key: "monthly_debts", label: "Monthly debts ($)", ph: "400", type: "number" },
              { key: "monthly_income", label: "Monthly income ($)", ph: "6500", type: "number" },
            ].map((f) => (
              <div key={f.key}>
                <Label className="text-xs uppercase tracking-widest text-[#6D716C] font-mono">{f.label}</Label>
                <Input
                  type={f.type}
                  placeholder={f.ph}
                  value={form[f.key]}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  data-testid={`mortgage-${f.key}`}
                  className="mt-1 rounded-sm font-mono"
                />
              </div>
            ))}
            <div className="md:col-span-5 flex justify-end">
              <Button
                type="submit"
                disabled={loading}
                data-testid="mortgage-run-btn"
                className="rounded-full btn-lime h-11 px-6"
              >
                {loading ? (
                  <>
                    <Sparkles size={15} className="mr-2 animate-pulse" /> Modeling…
                  </>
                ) : (
                  <>
                    <Home size={15} className="mr-2" /> Run simulation
                  </>
                )}
              </Button>
            </div>
          </form>

          {result && (
            <div className="fade-in space-y-6" data-testid="mortgage-result">
              <div className="card-surface p-8 relative overflow-hidden">
                <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#D5FF3F]/10 blur-3xl" />
                <div className="relative">
                  <div className="overline mb-2">Analysis</div>
                  <p className="text-lg text-[#F5F3EE] leading-relaxed max-w-3xl">{result.summary}</p>
                  <div className="mt-6 flex flex-wrap items-center gap-6 text-sm">
                    <div>
                      <div className="overline mb-1">DTI</div>
                      <div className="ticker text-2xl text-[#F5F3EE]">{result.dti_pct?.toFixed(1)}%</div>
                    </div>
                    {result.months_to_ready_estimate != null && (
                      <div>
                        <div className="overline mb-1">Time to ready</div>
                        <div className="ticker text-2xl text-[#D5FF3F]">{result.months_to_ready_estimate} mo</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {["conventional", "fha"].map((k) => {
                  const loan = result[k];
                  if (!loan) return null;
                  return (
                    <div key={k} className="card-surface p-6" data-testid={`loan-card-${k}`}>
                      <div className="flex items-center justify-between mb-4">
                        <div className="font-display text-2xl capitalize">{k}</div>
                        {loan.eligible ? (
                          <span className="text-xs px-2 py-0.5 rounded-full font-mono uppercase tracking-widest bg-[#D5FF3F] text-[#0B0C0E]">
                            <CheckCircle2 size={11} className="inline mr-1" /> Eligible
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded-full font-mono uppercase tracking-widest bg-[#FF6B4A]/15 text-[#FF6B4A]">
                            Not yet · {loan.gap_points || 0}pt gap
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <div className="overline mb-1">Est. rate</div>
                          <div className="ticker text-3xl text-[#F5F3EE]">{loan.est_rate_pct?.toFixed(2)}%</div>
                        </div>
                        <div>
                          <div className="overline mb-1">Est. P&I</div>
                          <div className="ticker text-3xl text-[#F5F3EE]">${Math.round(loan.est_monthly_pi).toLocaleString()}</div>
                        </div>
                      </div>
                      {loan.blocker && (
                        <div className="text-xs text-[#FF6B4A] flex items-start gap-2 font-mono">
                          <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
                          {loan.blocker}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {result.recommendations?.length > 0 && (
                <div className="card-surface p-8">
                  <div className="overline mb-6 flex items-center gap-2">
                    <TrendingUp size={13} /> Next-best moves
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    {result.recommendations.map((r, i) => (
                      <div key={`${i}-${(r.title || "rec").slice(0, 32)}`} className="border border-[#26282C] rounded-sm p-4">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="font-medium text-[#F5F3EE]">{r.title}</div>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono uppercase tracking-widest ${
                            r.impact === "high" ? "bg-[#D5FF3F] text-[#0B0C0E]" :
                            r.impact === "medium" ? "bg-[#7CFF9B]/15 text-[#7CFF9B]" :
                            "bg-[#26282C] text-[#A8ADA5]"
                          }`}>
                            {r.impact}
                          </span>
                        </div>
                        <p className="text-sm text-[#A8ADA5] leading-relaxed">{r.detail}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </AppShell>
  );
}
